import styled from 'styled-components';

import { CalendarDateIcon } from '../../assets';
import { ThemeProp } from '../../theme/Themes';
import { defaultColumnWidth } from '../../views/listings/Listing/ListingCardsTable/ListingCardsTable.styles';
import { Button, SecondaryButton, Text } from '..';

export const fiveColumnsBreakpoint = '1308px';
export const mobileBreakdown = '610px';

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: nowrap;
  position: relative;
  background-color: ${(props: ThemeProp) => props.theme.colors.black};
  box-shadow: ${(props: ThemeProp) => props.theme.shadows.primary};
  padding: 1.5rem 1rem 1.5rem 1rem;
  border-radius: 1rem;
  gap: 1rem;

  &:hover {
    box-shadow: ${(props: ThemeProp) => props.theme.shadows.hover};
  }

  @media (min-width: ${mobileBreakdown}) {
    padding: 1rem 1.5rem 1rem 1.5rem;
  }
`;

export const VehicleImageContainer = styled.div<any>`
  display: none;
  min-width: 6.52rem;
  width: 6.52rem;
  height: 7.125rem;
  min-height: 7.125rem;
  border-radius: 0.125rem;

  @media (min-width: ${fiveColumnsBreakpoint}) {
    ${(props) => props.display && 'display: flex;'}
  }
`;

export const VehicleSmallImageContainer = styled.div<any>`
  min-width: 6rem;
  width: 6rem;
  height: 5rem;
  min-height: 5rem;
  border-radius: 0.125rem;
  ${(props) => (props.display ? 'display: flex;' : 'display: none;')}

  @media (min-width: ${fiveColumnsBreakpoint}) {
    display: none;
  }
`;

export const VehicleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.125rem;
`;

export const VehicleInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1rem;
  position: relative;
`;

export const VehicleHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-grow: 1;
  min-height: 2rem;
  gap: 1rem;

  @media (min-width: ${fiveColumnsBreakpoint}) {
    align-items: center;
  }
`;

interface VehicleBodyContainerProps {
  $numberOfBoxes: number;
}
export const VehicleBodyContainer = styled.div<VehicleBodyContainerProps>`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  > div {
    // 1rem is the defined gap between boxes
    width: calc(
      (100% / ${(props: VehicleBodyContainerProps) => props.$numberOfBoxes}) -
        (1rem * (${(props: VehicleBodyContainerProps) => props.$numberOfBoxes} - 1))
    );
  }
`;

interface VehicleInfoContainerProps {
  $minWidth?: number;
  $maxWidth?: number;
}

export const VehicleInfoBoxContainer = styled.div<VehicleInfoContainerProps>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: ${(props: VehicleInfoContainerProps) => `${props.$minWidth ?? defaultColumnWidth}rem`};
  ${(props: VehicleInfoContainerProps) => `max-width: ${props.$maxWidth}rem`};
  overflow: hidden;
  align-items: flex-start;
`;

export const InfoBoxImageAndValuesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 0.5rem;
`;

export const InfoBoxImagesContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 0.25rem;
`;

export const InfoBoxImageContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface InfoBoxValuesContainerProps {
  $type?: string;
  $noGap?: boolean;
}
export const InfoBoxValuesContainer = styled.div<InfoBoxValuesContainerProps>`
  display: flex;
  flex-direction: column;
  max-width: ${(props: InfoBoxValuesContainerProps) => (props.$type === 'company' ? `calc(100% - 2.625rem)` : '100%')};
  flex: 1;
`;

export const InfoBoxValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const InfoBoxDateValueContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  }
`;

interface InfoBoxIconProps {
  $clickable?: boolean;
  $size?: string;
}
export const InfoBoxIcon = styled.div<InfoBoxIconProps>`
  display: flex;
  min-width: 0.875rem;
  width: ${(props) => props.$size ?? '0.875rem'};
  min-height: 0.875rem;
  height: ${(props) => props.$size ?? '0.875rem'};
  ${(props) => props.$clickable && 'cursor: pointer;'}
  ${(props) => props.$clickable && 'margin-right: auto;'}
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c600};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const InfoBoxTitle = styled(Text)`
  font-size: 0.688rem;
  line-height: 1.25rem;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  text-transform: uppercase;
  letter-spacing: 1px;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const TextWithActionBoxTitle = styled(InfoBoxTitle)`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    color: ${(props: ThemeProp) => props.theme.colors.greyscale.c500};
  }
`;

export const InfoBoxValue = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: 1rem;
  color: ${(props: { color: string } & ThemeProp) => props.color || props.theme.colors.neutrals.c800};

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  max-width: 300px;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
`;

export const InfoDateBoxValue = styled(InfoBoxValue)`
  color: ${(props: ThemeProp) => props.theme.colors.white};
  text-align: end;
`;

export const InfoBoxMoneyValue = styled(Text)`
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.h3.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.h3.lineHeight};
  color: ${(props: { color: string } & ThemeProp) => props.color || props.theme.colors.white};

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const VehicleHeaderInformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: wrap;

  @media (min-width: ${fiveColumnsBreakpoint}) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
  }
`;

export const VehicleHeaderActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  gap: 0.75rem;
  flex-wrap: nowrap;
`;

export const VehicleTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};

  flex-wrap: nowrap;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${(props: ThemeProp) => props.theme.colors.white};

  @media (min-width: ${fiveColumnsBreakpoint}) {
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.xl};
  }
`;

export const VehicleSubTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  flex-wrap: nowrap;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media (min-width: ${fiveColumnsBreakpoint}) {
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.xl};
  }
`;

export const VehicleStatusPill = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  height: 1.5rem;
  margin-top: 0.5rem;
  padding: 0rem 0.5rem;
  border-radius: 1.5rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  line-height: ${(props: ThemeProp) => props.theme.fontSize.ml};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};

  @media (min-width: ${fiveColumnsBreakpoint}) {
    margin-top: 0rem;
    margin-left: 0.5rem;
  }
`;

export const VariantOneStatusPill = styled(VehicleStatusPill)`
  background: #ffeeb2;
  color: #926002;
`;

export const VariantTwoStatusPill = styled(VehicleStatusPill)`
  background: #dfffe1;
  color: #1c8221;
`;

export const VariantThreeStatusPill = styled(VehicleStatusPill)`
  background: #ffe8ec;
  color: #bd1232;
`;

export const VariantFourStatusPill = styled(VehicleStatusPill)`
  background: #e0edff;
  color: #164fa3;
`;

export const ButtonsContainer = styled.div`
  display: none;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  gap: 0.75rem;
  flex-wrap: nowrap;

  @media (min-width: ${mobileBreakdown}) {
    display: flex;
  }
`;

export const ButtonsMobileContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  gap: 1rem;
  flex-wrap: nowrap;

  @media (min-width: ${mobileBreakdown}) {
    display: none;
  }
`;

export const StyledButton = styled(Button)`
  flex-grow: 1;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  padding: 0.688rem 1.141rem;

  @media (min-width: ${mobileBreakdown}) {
    flex-grow: 0;
    padding: 0.5rem 1rem;
    height: 2rem;
  }
`;

export const StyledSecondaryButton = styled(SecondaryButton)`
  flex-grow: 1;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  padding: 0.688rem 1.141rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  @media (min-width: ${mobileBreakdown}) {
    flex-grow: 0;
    padding: 0.5rem 1rem;
    height: 2rem;

    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
`;

export const NotDisplayedImagesIcon = styled.div`
  width: 2.125rem;
  height: 2.125rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.primary.c600};
  border-radius: 3.125rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c200};

  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
`;

export const PeopleTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #f4f4f4;
  border-radius: 0.5rem;
  gap: 0.5rem;
  padding: 1rem;
`;

export const InfoBoxChipsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 0.25rem;
`;

export const Chip = styled.div`
  font-weight: ${(props) => props.theme.fontWeight.regular};
  font-size: ${(props) => props.theme.fontSize.xs};
  line-height: ${(props) => props.theme.lineHeight.xs};
  padding: 0.438rem 0.625rem;
  border: 1px solid ${(props) => props.theme.colors.greyscale.c300};
  background-color: ${(props) => props.theme.colors.greyscale.c100};
  color: ${(props) => props.theme.colors.greyscale.c700};
  border-radius: 1.5rem;
`;

export const NotDisplayedChipsIcon = styled(NotDisplayedImagesIcon)`
  color: ${(props: ThemeProp) => props.theme.colors.primary.c700};
`;

export const StyledCalendarDateIcon = styled(CalendarDateIcon)`
  width: 0.875rem;
  height: 0.875rem;
`;
